import {useCallback, useEffect, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {AnimatePresence, motion, useReducedMotion, Variants} from 'framer-motion'
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RotateCcw,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import {Fechamento, FechamentoDetail, getFechamento, loadFechamentoImage} from './fechamentosApi'

type Props = {closing: Fechamento | FechamentoDetail; onClose: () => void}

// Páginas mantidas em memória ao redor da atual (as demais têm a URL revogada).
const CACHE_RADIUS = 3

const slideVariants: Variants = {
  enter: (direction: number) => ({
    x: direction >= 0 ? '55%' : '-55%',
    rotateY: direction >= 0 ? -38 : 38,
    scale: 0.82,
    opacity: 0,
    filter: 'blur(14px) brightness(1.6)',
  }),
  center: {
    x: 0,
    rotateY: 0,
    scale: 1,
    opacity: 1,
    filter: 'blur(0px) brightness(1)',
    transition: {
      x: {type: 'spring', stiffness: 120, damping: 20, mass: 0.9},
      rotateY: {type: 'spring', stiffness: 110, damping: 18},
      scale: {type: 'spring', stiffness: 140, damping: 20},
      opacity: {duration: 0.35},
      filter: {duration: 0.55, ease: 'easeOut'},
    },
  },
  exit: (direction: number) => ({
    x: direction >= 0 ? '-45%' : '45%',
    rotateY: direction >= 0 ? 32 : -32,
    scale: 0.78,
    opacity: 0,
    filter: 'blur(10px) brightness(0.6)',
    transition: {duration: 0.5, ease: [0.4, 0, 0.2, 1]},
  }),
}

const fadeVariants: Variants = {
  enter: {opacity: 0},
  center: {opacity: 1, transition: {duration: 0.2}},
  exit: {opacity: 0, transition: {duration: 0.2}},
}

export default function FechamentoViewer({closing, onClose}: Props) {
  const [detail, setDetail] = useState<FechamentoDetail>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [direction, setDirection] = useState(1)
  const [zoom, setZoom] = useState(1)
  const [imageUrl, setImageUrl] = useState('')
  const [imagePage, setImagePage] = useState(0)
  const [loading, setLoading] = useState(true)
  const [imageLoading, setImageLoading] = useState(false)
  const [detailError, setDetailError] = useState('')
  const [imageError, setImageError] = useState('')
  const [retry, setRetry] = useState(0)
  const reduceMotion = useReducedMotion()

  const cacheRef = useRef(new Map<number, string>())
  const inflightRef = useRef(new Map<number, Promise<string>>())
  const controllerRef = useRef<AbortController>(null)

  useEffect(() => {
    const controller = new AbortController()
    const cache = cacheRef.current
    const inflight = inflightRef.current
    controllerRef.current = controller
    return () => {
      controller.abort()
      cache.forEach((url) => URL.revokeObjectURL(url))
      cache.clear()
      inflight.clear()
    }
  }, [])

  const goTo = useCallback(
    (target: number) => {
      const next = Math.min(closing.pageCount, Math.max(1, target))
      if (next === pageNumber) return
      setDirection(next > pageNumber ? 1 : -1)
      setPageNumber(next)
      setZoom(1)
    },
    [closing.pageCount, pageNumber]
  )

  const fetchPage = useCallback(
    (page: number) => {
      const cached = cacheRef.current.get(page)
      if (cached) return Promise.resolve(cached)
      let pending = inflightRef.current.get(page)
      if (!pending) {
        const signal = controllerRef.current.signal
        pending = loadFechamentoImage(closing.id, page, false, signal)
          .then((blob) => {
            if (signal.aborted) throw new DOMException('Aborted', 'AbortError')
            const url = URL.createObjectURL(blob)
            cacheRef.current.set(page, url)
            return url
          })
          .finally(() => inflightRef.current.delete(page))
        inflightRef.current.set(page, pending)
      }
      return pending
    },
    [closing.id]
  )

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setDetailError('')
    const operation =
      'pages' in closing ? Promise.resolve(closing) : getFechamento(closing.id, controller.signal)
    operation
      .then((data) => {
        if (controller.signal.aborted) return
        setDetail(data)
        setLoading(false)
      })
      .catch((error) => {
        if (controller.signal.aborted) return
        setDetailError(error.message)
        setLoading(false)
      })
    return () => controller.abort()
  }, [closing, retry])

  useEffect(() => {
    if (!detail || detail.id !== closing.id) return
    let active = true
    setImageError('')
    const cached = cacheRef.current.get(pageNumber)
    if (cached) {
      setImageUrl(cached)
      setImagePage(pageNumber)
      setImageLoading(false)
    } else {
      setImageLoading(true)
    }
    fetchPage(pageNumber)
      .then((url) => {
        if (!active) return
        setImageUrl(url)
        setImagePage(pageNumber)
        setImageLoading(false)
        cacheRef.current.forEach((cachedUrl, page) => {
          if (Math.abs(page - pageNumber) <= CACHE_RADIUS) return
          URL.revokeObjectURL(cachedUrl)
          cacheRef.current.delete(page)
        })
        // Pré-carrega as vizinhas para a transição seguinte ser instantânea.
        for (const neighbor of [pageNumber + 1, pageNumber - 1]) {
          if (neighbor >= 1 && neighbor <= closing.pageCount) fetchPage(neighbor).catch(() => {})
        }
      })
      .catch((error) => {
        if (!active || controllerRef.current.signal.aborted) return
        setImageError(error.message)
        setImageLoading(false)
      })
    return () => {
      active = false
    }
  }, [closing.id, closing.pageCount, detail, fetchPage, pageNumber, retry])

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement
      if (
        target instanceof HTMLElement &&
        target.closest('input, select, textarea, [contenteditable="true"]')
      )
        return
      if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
        event.preventDefault()
        goTo(pageNumber - 1)
      }
      if (event.key === 'ArrowRight' || event.key === 'PageDown') {
        event.preventDefault()
        goTo(pageNumber + 1)
      }
      if (event.key === 'Home') {
        event.preventDefault()
        goTo(1)
      }
      if (event.key === 'End') {
        event.preventDefault()
        goTo(closing.pageCount)
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [closing.pageCount, goTo, pageNumber])

  const currentPage = detail?.pages.find((page) => page.pageNumber === pageNumber)
  const pageError = detailError || imageError
  const imageIsCurrent = imagePage === pageNumber
  const showSlide = !loading && !imageLoading && !pageError && imageIsCurrent && !!imageUrl
  const variants = reduceMotion ? fadeVariants : slideVariants
  const progress = closing.pageCount > 0 ? pageNumber / closing.pageCount : 0

  return (
    <Modal
      show
      onHide={onClose}
      centered
      className='fechamentos-viewer'
      dialogClassName='fechamentos-viewer-dialog'
      aria-labelledby='fechamentos-viewer-title'
    >
      <Modal.Header className='fechamentos-viewer-header'>
        <div className='fechamentos-viewer-heading'>
          <span>VISUALIZADOR DE FECHAMENTO</span>
          <h2 id='fechamentos-viewer-title'>{closing.title}</h2>
        </div>
        <div className='fechamentos-viewer-tools'>
          <span className='fechamentos-image-info'>
            {currentPage
              ? currentPage.width + ' × ' + currentPage.height + ' px · ' + currentPage.dpi + ' dpi'
              : ''}
          </span>
          <div className='fechamentos-zoom'>
            <button
              type='button'
              className='fechamentos-icon-button'
              aria-label='Diminuir zoom'
              onClick={() => setZoom((value) => Math.max(0.5, value - 0.25))}
              disabled={zoom <= 0.5}
            >
              <ZoomOut size={18} />
            </button>
            <span>{Math.round(zoom * 100)}%</span>
            <button
              type='button'
              className='fechamentos-icon-button'
              aria-label='Aumentar zoom'
              onClick={() => setZoom((value) => Math.min(3, value + 0.25))}
              disabled={zoom >= 3}
            >
              <ZoomIn size={18} />
            </button>
            <button
              type='button'
              className='fechamentos-icon-button'
              aria-label='Ajustar à tela'
              onClick={() => setZoom(1)}
            >
              <RotateCcw size={17} />
            </button>
          </div>
          <button
            type='button'
            className='fechamentos-icon-button'
            onClick={onClose}
            aria-label='Fechar visualizador'
          >
            <X size={22} />
          </button>
        </div>
      </Modal.Header>
      <div className='fechamentos-viewer-progress' aria-hidden='true'>
        <motion.span
          initial={false}
          animate={{scaleX: progress}}
          transition={{type: 'spring', stiffness: 90, damping: 18}}
        />
      </div>
      <Modal.Body className='fechamentos-viewer-stage'>
        <div className='fechamentos-viewer-glow' aria-hidden='true' />
        <AnimatePresence initial={false} custom={direction}>
          {showSlide && (
            <motion.div
              key={imagePage}
              className={'fechamentos-viewer-slide' + (zoom > 1 ? ' zoomed' : '')}
              custom={direction}
              variants={variants}
              initial='enter'
              animate='center'
              exit='exit'
            >
              <div className='fechamentos-viewer-frame'>
                <img
                  src={imageUrl}
                  alt={'Página ' + imagePage + ' de ' + closing.title}
                  style={
                    zoom === 1
                      ? undefined
                      : {
                          width: 'calc(' + zoom + ' * (100cqw - 2 * var(--slide-pad-x)))',
                          maxWidth: 'none',
                          maxHeight: 'none',
                        }
                  }
                  onError={() =>
                    setImageError('Não foi possível exibir esta página. Tente novamente.')
                  }
                />
                {!reduceMotion && (
                  <motion.span
                    className='fechamentos-viewer-shine'
                    aria-hidden='true'
                    initial={{x: '-120%'}}
                    animate={{x: '220%'}}
                    transition={{duration: 1.1, delay: 0.25, ease: [0.25, 0.1, 0.25, 1]}}
                  />
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {(loading || imageLoading || (!pageError && !imageIsCurrent)) && (
            <motion.div
              key='loading'
              className='fechamentos-viewer-placeholder'
              role='status'
              initial={{opacity: 0}}
              animate={{opacity: 1, transition: {delay: 0.15}}}
              exit={{opacity: 0}}
            >
              <Loader2 size={30} className='fechamentos-spin' /> Carregando página...
            </motion.div>
          )}
        </AnimatePresence>
        {pageError && (
          <div className='fechamentos-viewer-placeholder' role='alert'>
            <AlertCircle size={30} />
            <p>{pageError}</p>
            <button
              className='fechamentos-button secondary'
              onClick={() => setRetry((value) => value + 1)}
            >
              Tentar novamente
            </button>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className='fechamentos-viewer-footer'>
        <button
          type='button'
          className='fechamentos-button secondary'
          onClick={() => goTo(pageNumber - 1)}
          disabled={pageNumber === 1 || loading}
        >
          <ChevronLeft size={18} /> Anterior
        </button>
        <div className='fechamentos-page-select'>
          <label htmlFor='fechamentos-page-number'>Página</label>
          <div className='fechamentos-page-counter'>
            <AnimatePresence initial={false} mode='popLayout' custom={direction}>
              <motion.span
                key={pageNumber}
                custom={direction}
                initial={{y: direction >= 0 ? 18 : -18, opacity: 0}}
                animate={{y: 0, opacity: 1}}
                exit={{y: direction >= 0 ? -18 : 18, opacity: 0}}
                transition={{type: 'spring', stiffness: 300, damping: 24}}
                aria-hidden='true'
              >
                {String(pageNumber).padStart(2, '0')}
              </motion.span>
            </AnimatePresence>
            <select
              id='fechamentos-page-number'
              value={pageNumber}
              onChange={(event) => goTo(Number(event.target.value))}
              disabled={loading}
            >
              {Array.from({length: closing.pageCount}, (_, index) => (
                <option key={index + 1} value={index + 1}>
                  {index + 1}
                </option>
              ))}
            </select>
          </div>
          <span>de {String(closing.pageCount).padStart(2, '0')}</span>
        </div>
        <button
          type='button'
          className='fechamentos-button secondary'
          onClick={() => goTo(pageNumber + 1)}
          disabled={pageNumber >= closing.pageCount || loading}
        >
          Próxima <ChevronRight size={18} />
        </button>
      </Modal.Footer>
    </Modal>
  )
}
